/**
 * The unit catalogue.
 *
 * Everything the simulation needs to instantiate a soldier lives here, plus a
 * `vis` block the procedural renderer reads to draw one. No art assets: `vis`
 * *is* the art, expressed as silhouette parameters.
 *
 * ── What `UF.Flying` actually does, before you author around it ──────────────
 *
 * Written down here because the honest answer is the difference between a
 * roster and twelve units whose defining trait is decorative. Flying was
 * **fully wired and completely unpopulated**: until this pass not one of the
 * sixty-two entries below carried the flag, and `render/textures.ts` had a
 * finished `drawFlier` — swept wings, a wing-beat frame, its own token — that
 * nothing in the game had ever asked it to draw. Six systems already read the
 * bit:
 *
 *   `systems/movement.ts`   a flier is out of crowd separation entirely, and
 *                           the collide pass never tests it against solid
 *                           geometry — walls, gates, towers, buildings, water,
 *                           cliffs and rubble are all simply not there
 *   `world.ts`              never snapped off a blocked tile at birth, never
 *                           evicted when something is stamped underneath it
 *   `systems/garrison.ts`   counts as a climber, so it is one of the four
 *                           things that can reach a man standing on an intact
 *                           curtain (the others are a bow, a ladder, an engine)
 *   `systems/towers.ts`     only a tower whose `attack.hitsFliers` is true may
 *                           shoot at it. The Arrow Tower and the Watchtower
 *                           both can, from the first Age, so this is answerable
 *                           by anybody on the wave they first meet it
 *   `systems/combat.ts`     an invading flier never takes a wall or a building
 *                           as a target — it is an anti-*unit* weapon and
 *                           cannot knock a town down
 *   `systems/director.ts`   fliers are half of what the horde buys when it
 *                           reads the player as leaning on a choke point
 *
 * BRIEF.md's own line is "fliers ignore walls entirely", and that is exactly
 * and only what the flag delivers. **What it is not is evasion.** There is no
 * air/ground distinction in `stepTargeting`: a spearman on the ground picks a
 * gryphon overhead out of the spatial hash exactly as if it were standing next
 * to him, and hits it. So these are costed as *mobility* — a company that
 * arrives where the wall is not — and never as a company that is hard to
 * touch once it has arrived. If somebody later adds the reach test, every
 * threat number in the flying block below is too low and should be re-read.
 */

import { ArmourType, DamageType, UF, type ResourceBundle, type UnitVoice } from '../sim/types.ts';
import { componentCost } from './buildings.ts';
import { HERO_DAMAGE_TYPE, HERO_UNIT_FLAGS } from './heroes.ts';

export type VisBuild =
  | 'villager'
  | 'infantry'
  | 'archer'
  | 'cavalry'
  | 'brute'
  | 'siege'
  | 'caster'
  | 'swarm'
  | 'flier'
  | 'champion';

export type VisWeapon =
  | 'sword'
  | 'spear'
  | 'axe'
  | 'bow'
  | 'crossbow'
  | 'gun'
  | 'staff'
  | 'hammer'
  | 'claw'
  | 'scythe'
  | 'none';

export interface UnitVisual {
  build: VisBuild;
  /** Silhouette scale multiplier. WoW rules: big shoulders, big weapons. */
  scale: number;
  weapon: VisWeapon;
  shield: boolean;
  /** Head/helm silhouette variant, 0-5. */
  helm: number;
  /** Shoulder mass, 0-2. Drives how chunky the silhouette reads at zoom. */
  pauldron: number;
  cloak: boolean;
  banner: boolean;
}

export interface UnitType {
  id: string;
  name: string;
  flavour: string;
  faction: string;
  /** Age gate: 1 = available immediately, 4 = late. */
  tier: number;
  /**
   * The tech node that unlocks this company, or absent for the roster's
   * default. Refused at `musterRoom`, the one door every muster goes through,
   * with the node's own name in the refusal — and walked by `tests/tech.test.ts`
   * so a gate can never name a node that does not exist. See the same field on
   * `BuildingType` for the bug that taught this shape.
   */
  tech?: string;

  hp: number;
  /** Tiles per second. */
  speed: number;
  radius: number;
  armour: number;

  damage: number;
  dmgType: number;
  /** Tiles. <= 0.8 counts as melee. */
  range: number;
  attacksPerSec: number;
  /** Fraction of the attack cycle spent winding up before the contact frame. */
  windup: number;
  /** Splash radius in tiles; 0 for single target. */
  splash: number;

  flags: number;
  /**
   * What one model costs, in resources — **components already folded in**.
   *
   * `u()` expands `parts` into this at construction, so every call site in the
   * game that ever read `t.cost` is reading the true muster price and none of
   * them had to learn what a frame is. See `COMPONENTS` in `buildings.ts`.
   */
  cost: Partial<ResourceBundle>;
  /**
   * Finished war components consumed from inventory to build one, by component id.
   *
   * Kept on the type after folding purely so the codex and the muster tooltip
   * can say "four frames, six fittings and two gilded cores". These are real
   * stored goods produced by workshops before the unit can be assembled.
   */
  parts?: Record<string, number>;
  pop: number;
  /**
   * The company's **establishment** — how many men are on its roll at full
   * strength, not how many the town can pay for today. See `COMPANY_POINTS`.
   */
  companySize: number;
  /** Shots carried. 0 for anything that is not a missile troop. */
  ammo: number;
  /** How quickly this unit tires, relative to a line infantryman. */
  stamina: number;
  /** Director budget value. Roughly "how much trouble is this". */
  threat: number;
  /** Optional special behaviour hook, resolved in the combat systems. */
  ability?: string;
  /**
   * What this unit sounds like when struck. Derived from flags and armour at
   * `u()` unless the spec authors one — see `deriveVoice` for the order the
   * defaults are decided in. Read by the audio layer's impact recipes.
   */
  voice: UnitVoice;
  vis: UnitVisual;
}

/**
 * What a company of rank and file is worth, in the points the rest of the game
 * spends.
 *
 * **Every budget outside the settlement is denominated in `threat ×
 * companySize`** and nothing else: `battle.ts`'s `companyPoints`, the campaign's
 * `buildRegiments`, the muster panel's price, the director's pack cost. So the
 * establishment is not a free number — it is the *denomination of the coin* all
 * of those budgets are counted in, and moving it moves how many blocks a purse
 * buys everywhere at once.
 *
 * This pass set every rank-and-file establishment to roughly four hundred
 * points, which is what turns a company from a squad into a regiment you can
 * read from the camera: line infantry went 14 → 40, levy 26 → 100, missile
 * 12 → 31, heavy foot 12 → 18. The arithmetic is deliberately the same one the
 * budgets use, so **a given purse still buys the same number of men** — it buys
 * them in fewer, larger blocks. A wave budget that bought twenty packs of
 * twelve now buys six blocks of forty, and the model count in a siege did not
 * move (measured; see BALANCE.md pass 8).
 *
 * Three things are exempt, and the exemption is the WH3 shape rather than an
 * accident: `UF.Large`, `UF.Siege`, `UF.Champion` and `UF.Caster` are single
 * entities or crews — a monster is one monster, a Knell Engine is one engine, a
 * Confessor is a man with a book — and villagers and summons are not companies
 * at all. Those keep their authored establishment, which is also what keeps a
 * company under `MIN_ARMY_POINTS` on every roster: the campaign's smallest
 * muster still has something it can afford.
 *
 * The ceiling is the other half of the rule. Nothing may pass **1200 points**,
 * the dearest company that has ever existed (a company of twelve Hollowed), and
 * `tests/roster.test.ts` holds the line: past that the campaign's own muster
 * cannot buy one at any budget it is allowed to raise, which is unreachable
 * content wearing a price tag. That ceiling is why an elite establishment barely
 * moves and a levy's quadruples — the cheap are many and the dear are few, which
 * is the shape the ask wanted anyway.
 */
export const COMPANY_POINTS = 400;

/**
 * Wages, in gold per minute per model.
 *
 * Derived from `threat` rather than authored per unit, so that adding a soldier
 * to the catalogue cannot accidentally create one that is free to keep. A
 * knight costing four times what a militiaman costs to raise also costs four
 * times as much to *keep*, and that recurring bill is what stops the correct
 * answer from being "recruit everything and never disband".
 */
export const WAGE_PER_THREAT_PER_MIN = 0.11;

export function unitWage(t: UnitType): number {
  // Villagers are taxed, not paid; the summoned and the risen do not eat.
  if ((t.flags & (UF.Villager | UF.Summoned | UF.Undead)) !== 0) return 0;
  return t.threat * WAGE_PER_THREAT_PER_MIN;
}

type UnitSpec = Omit<Partial<UnitType>, 'vis'> & {
  id: string;
  name: string;
  flavour: string;
  faction: string;
  vis: Partial<UnitVisual> & { build: VisBuild };
};

/**
 * The default voice, from what the catalogue already says a unit is.
 *
 * Order matters and is written narrowest-claim-first: the risen clack whatever
 * size they are, an engine creaks whatever it carries, a monster is a monster
 * before it is a suit of plate. Author `voice` on the spec to override — a
 * chitinous horror or a stone golem is a flavour call the flags cannot make.
 */
function deriveVoice(flags: number, armour: number): UnitVoice {
  if ((flags & UF.Undead) !== 0) return 'bone';
  if ((flags & UF.Siege) !== 0) return 'timber';
  if ((flags & UF.Large) !== 0) return 'beast';
  if ((flags & UF.Flying) !== 0) return 'spirit';
  if (armour >= ArmourType.Plate && armour !== ArmourType.Monstrous) return 'metal';
  return 'flesh';
}

function u(spec: UnitSpec): UnitType {
  const melee = (spec.range ?? 0.55) <= 0.8;
  /**
   * Components are folded here and nowhere else.
   *
   * The alternative was a `unitCost(t)` helper and six call sites — two in
   * `companies.ts`, two in `bots.ts`, two in the HUD — and the failure mode of
   * missing one of them is a soldier that displays one price and charges
   * another. Folding at construction means there is exactly one price, it is the
   * one everybody already reads, and a component cannot drift away from the
   * thing it is a component of.
   */
  const parts = spec.parts;
  let cost = spec.cost ?? {};
  if (parts) {
    const extra = componentCost(parts);
    cost = { ...cost };
    for (const k of Object.keys(extra) as Array<keyof ResourceBundle>) {
      cost[k] = (cost[k] ?? 0) + (extra[k] ?? 0);
    }
  }
  // Equipped late-age faction soldiers and riders draw arms from the same
  // workshops as the common line. Unarmed beasts, spirits and casters retain
  // their own economies; free director-only entries keep their authored cost.
  if ((spec.tier ?? 1) >= 3 && spec.faction !== 'common' && Object.keys(cost).length > 0 &&
    ['infantry', 'archer', 'cavalry', 'champion', 'brute', 'flier'].includes(spec.vis.build) &&
    !['none', 'claw', 'staff'].includes(spec.vis.weapon ?? 'none')) {
    cost = { ...cost, armaments: (cost.armaments ?? 0) + (spec.tier ?? 3) - 1 };
  }
  return {
    id: spec.id,
    name: spec.name,
    flavour: spec.flavour,
    faction: spec.faction,
    tier: spec.tier ?? 1,
    tech: spec.tech,
    hp: spec.hp ?? 90,
    speed: spec.speed ?? 1.5,
    radius: spec.radius ?? 0.28,
    armour: spec.armour ?? ArmourType.Leather,
    damage: spec.damage ?? 9,
    dmgType: spec.dmgType ?? (melee ? DamageType.Slash : DamageType.Arrow),
    range: spec.range ?? 0.55,
    attacksPerSec: spec.attacksPerSec ?? 0.85,
    windup: spec.windup ?? 0.42,
    splash: spec.splash ?? 0,
    flags: spec.flags ?? 0,
    cost,
    parts,
    pop: spec.pop ?? 1,
    companySize: spec.companySize ?? 12,
    // Missile troops get a quiver sized to their rate of fire: roughly forty
    // seconds of sustained shooting, so a firefight has an end.
    ammo:
      spec.ammo ??
      (((spec.flags ?? 0) & UF.Ranged) !== 0
        ? Math.max(6, Math.round((spec.attacksPerSec ?? 0.85) * 40))
        : 0),
    stamina: spec.stamina ?? 1,
    threat: spec.threat ?? 10,
    ability: spec.ability,
    voice: spec.voice ?? deriveVoice(spec.flags ?? 0, spec.armour ?? ArmourType.Leather),
    vis: {
      build: spec.vis.build,
      scale: spec.vis.scale ?? 1,
      weapon: spec.vis.weapon ?? 'sword',
      shield: spec.vis.shield ?? false,
      helm: spec.vis.helm ?? 0,
      pauldron: spec.vis.pauldron ?? 1,
      cloak: spec.vis.cloak ?? false,
      banner: spec.vis.banner ?? false,
    },
  };
}

export const UNIT_TYPES: UnitType[] = [
  // ── Common ───────────────────────────────────────────────────────────────
  u({
    id: 'villager',
    name: 'Villager',
    flavour: 'Counted at dawn, counted again at dusk, and the second number is the one that matters.',
    faction: 'common',
    hp: 42,
    speed: 1.75,
    radius: 0.22,
    armour: ArmourType.Unarmoured,
    damage: 3,
    attacksPerSec: 0.6,
    flags: UF.Villager,
    pop: 1,
    companySize: 1,
    threat: 1,
    vis: { build: 'villager', scale: 0.84, weapon: 'none', pauldron: 0 },
  }),
  /**
   * The Runeguard — the Kindling's line, and every faction's.
   *
   * Common rather than per-faction on purpose. The six houses disagree about
   * almost everything, but a plate of runestone bolted over mail works the same
   * way whoever is standing in it, and the point of the unit is the *bill*: it
   * is the first soldier in the game that cannot be raised out of grain and
   * timber. No magewood, no ingots, no Runeguard, whatever your banner says.
   *
   * Statted as a slow anvil rather than a better militia. It out-holds anything
   * in the Age-1 roster and out-runs nothing at all, so it changes what a line
   * can survive without changing what an army can chase.
   */
  u({
    id: 'runeguard',
    name: 'Runeguard',
    flavour: 'The plates were cut to fit a man. They have since been persuaded to hold one up.',
    faction: 'common',
    tier: 2,
    hp: 210,
    speed: 1.2,
    radius: 0.32,
    armour: ArmourType.Plate,
    damage: 17,
    dmgType: DamageType.Blunt,
    attacksPerSec: 0.75,
    flags: UF.Shielded,
    // Per model, like every cost here. A company of thirteen is thirteen times
    // this, and an Arcane Sawmill running flat out pays for roughly one company
    // an hour and a half.
    cost: { magewood: 6, ingots: 3, steel: 2, tools: 0.5, grain: 20 },
    companySize: 13,
    threat: 30,
    stamina: 0.85,
    vis: { build: 'infantry', weapon: 'hammer', shield: true, helm: 3, pauldron: 2, scale: 1.1 },
  }),

  /**
   * The upper Ages' line, and it is common for the same reason the Runeguard is.
   *
   * Six houses disagree about everything above the neck and about nothing at all
   * below it: soulwood is soulwood and alloy is alloy, and by the third Age the
   * thing that decides what a settlement can field is its chain rather than its
   * banner. Each of these is a *bill* first and a soldier second — every one of
   * them costs a good that did not exist an Age ago, so none of them can be
   * raised out of anything a settlement had before it climbed.
   *
   * They do not replace the Age-1 roster. A wall of militia is still a legitimate
   * answer to a wave, and it is now a cheap one. What these change is what a wall
   * of militia can be asked to *hold*.
   */
  u({
    id: 'voidsworn',
    name: 'Voidsworn',
    flavour: 'Sworn in a room with the shutters closed. They do not say to what, and nobody has asked twice.',
    faction: 'common',
    tier: 3,
    hp: 300,
    speed: 1.15,
    radius: 0.34,
    armour: ArmourType.Plate,
    damage: 30,
    dmgType: DamageType.Arcane,
    attacksPerSec: 0.7,
    flags: UF.Shielded,
    // Arcane out of a line unit, which is what the Bell buys: the armoured
    // waves that make an arrow tower look silly do not care about a sword either.
    cost: { soulwood: 8, alloy: 4, armaments: 3, grain: 26 },
    companySize: 8,
    pop: 2,
    threat: 52,
    stamina: 0.9,
    vis: { build: 'infantry', weapon: 'scythe', shield: true, helm: 5, pauldron: 2, cloak: true, scale: 1.14 },
  }),
  u({
    id: 'stormcaller',
    name: 'Stormcaller Engine',
    flavour: 'Four men, a winch and a ring turned to a thousandth. Three of the four are for the ring.',
    faction: 'common',
    tier: 3,
    hp: 260,
    speed: 0.78,
    radius: 0.46,
    armour: ArmourType.Structure,
    damage: 82,
    dmgType: DamageType.Arcane,
    range: 10.5,
    attacksPerSec: 0.24,
    windup: 0.68,
    splash: 1.4,
    flags: UF.Siege | UF.Ranged,
    // The carriage and arcane works must already have been fabricated.
    cost: { grain: 20 },
    parts: { frame: 3, fitting: 4, sigil_frame: 2, focusing_ring: 2 },
    pop: 4,
    companySize: 3,
    threat: 74,
    vis: { build: 'siege', weapon: 'none', scale: 1.3, pauldron: 0 },
  }),
  u({
    id: 'ashblade',
    name: 'Ashblade',
    flavour: 'The edge does not dull. The arm does, and they have arrangements about that.',
    faction: 'common',
    tier: 4,
    hp: 430,
    speed: 1.3,
    radius: 0.36,
    armour: ArmourType.Plate,
    damage: 46,
    dmgType: DamageType.Slash,
    attacksPerSec: 0.8,
    flags: UF.Shielded,
    cost: { ashglass: 6, soulwood: 6, armaments: 4, grain: 40 },
    companySize: 8,
    pop: 2,
    threat: 84,
    stamina: 1.1,
    vis: { build: 'infantry', weapon: 'sword', shield: true, helm: 4, pauldron: 2, cloak: true, scale: 1.2 },
  }),
  u({
    id: 'heartiron_colossus',
    name: 'Heartiron Colossus',
    flavour: 'It was cast around something. The founders were paid not to say what, and were paid well.',
    faction: 'common',
    tier: 4,
    hp: 1500,
    speed: 0.82,
    radius: 0.66,
    armour: ArmourType.Plate,
    damage: 128,
    dmgType: DamageType.Blunt,
    attacksPerSec: 0.45,
    splash: 1.5,
    flags: UF.Siege | UF.Large,
    cost: { grain: 40, souls: 40 },
    parts: { frame: 4, fitting: 6, gilded_core: 2 },
    pop: 6,
    companySize: 2,
    threat: 158,
    stamina: 1.4,
    vis: { build: 'brute', weapon: 'hammer', scale: 1.62, pauldron: 2, banner: true },
  }),
  /**
   * The Hollowed draw no wage and do not run.
   *
   * Both of those come off `UF.Undead`, and both are honest: they are not paid
   * because they have stopped asking, and they do not rout because the part that
   * would have is the part that was taken out. They still take a bed and still
   * eat, which is the trade the fifth Age offers — coin for grain, and a
   * settlement that has to keep feeding what it has already spent.
   *
   * Whether they are dead is not a question the game answers. The Hollow Gate
   * has a near arch and a far one and there is a queue at the near one.
   */
  u({
    id: 'hollowed',
    name: 'The Hollowed',
    flavour: 'Went in on the Thursday. Came out punctual, and has been punctual since.',
    faction: 'common',
    tier: 5,
    hp: 540,
    speed: 1.35,
    radius: 0.32,
    armour: ArmourType.Mail,
    damage: 48,
    dmgType: DamageType.Slash,
    attacksPerSec: 0.85,
    flags: UF.Undead | UF.Shielded,
    cost: { hollowglass: 7, armaments: 4, souls: 25, grain: 18 },
    companySize: 12,
    pop: 2,
    threat: 100,
    stamina: 2.2,
    vis: { build: 'infantry', weapon: 'sword', shield: true, helm: 5, pauldron: 1, cloak: true, scale: 1.05 },
  }),
  u({
    id: 'knell_engine',
    name: 'Knell Engine',
    flavour: 'It is tuned before every wave. The tuning takes an hour and is done by one man, alone.',
    faction: 'common',
    tier: 5,
    hp: 2300,
    speed: 0.7,
    radius: 0.74,
    armour: ArmourType.Structure,
    damage: 190,
    dmgType: DamageType.Arcane,
    range: 12.0,
    attacksPerSec: 0.2,
    windup: 0.7,
    splash: 2.2,
    flags: UF.Siege | UF.Ranged | UF.Large,
    cost: { grain: 30, souls: 60 },
    parts: { frame: 5, fitting: 6, focusing_ring: 4, hollow_heart: 2 },
    pop: 8,
    companySize: 1,
    threat: 250,
    vis: { build: 'siege', weapon: 'none', scale: 1.8, pauldron: 0, banner: true },
  }),

  // ── The Ashen Crown ──────────────────────────────────────────────────────
  u({
    id: 'ashen_militia',
    name: 'Hearth Militia',
    flavour: 'Given a shield, a name on a list, and eleven strangers to die beside.',
    faction: 'ashen',
    hp: 96,
    speed: 1.5,
    armour: ArmourType.Shielded,
    damage: 9,
    dmgType: DamageType.Slash,
    attacksPerSec: 0.9,
    flags: UF.Shielded,
    // Costs are *per model*, so a company of forty costs forty times this. The
    // first pass had them four times too expensive: the harness reached wave ten
    // with a full granary and not one soldier. Nothing here moved when the
    // establishment went from fourteen to forty — the town buys men, not
    // banners, and a muster takes as many as it can pay for. See COMPANY_POINTS.

    cost: { timber: 9, grain: 14 },
    companySize: 40,
    threat: 10,
    vis: { build: 'infantry', weapon: 'sword', shield: true, helm: 1, pauldron: 1 },
  }),
  u({
    id: 'ashen_spear',
    name: 'Ashfield Spears',
    flavour: 'They buried the sixth company standing, so the ranks would not look thin.',
    faction: 'ashen',
    hp: 88,
    speed: 1.45,
    armour: ArmourType.Leather,
    damage: 12,
    dmgType: DamageType.Pierce,
    range: 0.95,
    attacksPerSec: 0.72,
    windup: 0.5,
    flags: UF.Spear,
    cost: { timber: 12, iron: 4, grain: 12 },
    companySize: 33,
    threat: 12,
    ability: 'brace',
    vis: { build: 'infantry', weapon: 'spear', helm: 2, pauldron: 1 },
  }),
  u({
    id: 'ashen_archer',
    name: 'Crownwood Archers',
    flavour: 'Loose on the second bell. There is never a third.',
    faction: 'ashen',
    hp: 62,
    speed: 1.55,
    armour: ArmourType.Unarmoured,
    damage: 9,
    dmgType: DamageType.Arrow,
    range: 6.4,
    attacksPerSec: 0.72,
    windup: 0.55,
    flags: UF.Ranged,
    cost: { timber: 14, grain: 11 },
    companySize: 31,
    threat: 13,
    vis: { build: 'archer', weapon: 'bow', helm: 0, pauldron: 0, scale: 0.95 },
  }),
  u({
    id: 'ashen_outrider',
    name: 'Grey Outriders',
    flavour: 'The last horses in the realm, and they know it.',
    faction: 'ashen',
    tier: 2,
    hp: 165,
    speed: 3.1,
    radius: 0.36,
    armour: ArmourType.Mail,
    damage: 14,
    dmgType: DamageType.Slash,
    attacksPerSec: 0.8,
    flags: UF.Cavalry,
    cost: { timber: 30, iron: 30, grain: 55 },
    pop: 2,
    companySize: 15,
    threat: 26,
    ability: 'charge',
    vis: { build: 'cavalry', weapon: 'sword', helm: 1, pauldron: 1, cloak: true, scale: 1.05 },
  }),
  u({
    id: 'ashen_manatarms',
    name: 'Men-at-Arms',
    flavour: 'Professional. Which is to say: paid, and therefore expected to come back.',
    faction: 'ashen',
    tier: 2,
    hp: 150,
    speed: 1.4,
    armour: ArmourType.Mail,
    damage: 15,
    dmgType: DamageType.Slash,
    attacksPerSec: 0.85,
    flags: UF.Shielded,
    cost: { iron: 40, grain: 40 },
    companySize: 18,
    threat: 22,
    vis: { build: 'infantry', weapon: 'sword', shield: true, helm: 3, pauldron: 2, scale: 1.05 },
  }),
  u({
    id: 'ashen_arbalest',
    tech: 'war_arbalests',
    name: 'Arbalesters',
    flavour: 'A crank, a click, and a hole in something that was wearing very good armour.',
    faction: 'ashen',
    tier: 3,
    hp: 78,
    speed: 1.3,
    armour: ArmourType.Leather,
    damage: 26,
    dmgType: DamageType.Pierce,
    range: 7.2,
    attacksPerSec: 0.38,
    windup: 0.62,
    flags: UF.Ranged,
    cost: { timber: 30, iron: 40, grain: 30 },
    companySize: 15,
    threat: 27,
    vis: { build: 'archer', weapon: 'crossbow', helm: 2, pauldron: 1 },
  }),
  u({
    id: 'ashen_knight',
    tech: 'war_heavy_horse',
    name: 'Knights of the Guttering Flame',
    flavour: 'Nine remain. They ride as though there were ninety, because someone has to.',
    faction: 'ashen',
    tier: 3,
    hp: 300,
    speed: 2.9,
    radius: 0.4,
    armour: ArmourType.Plate,
    damage: 26,
    dmgType: DamageType.Blunt,
    attacksPerSec: 0.7,
    flags: UF.Cavalry | UF.Shielded,
    cost: { iron: 80, grain: 70, souls: 20 },
    pop: 3,
    companySize: 8,
    threat: 52,
    ability: 'charge',
    vis: { build: 'cavalry', weapon: 'hammer', shield: true, helm: 4, pauldron: 2, cloak: true, scale: 1.15 },
  }),
  u({
    id: 'ashen_bellwarden',
    name: 'Bellwarden',
    flavour: 'Keeps the hours. The hours have been getting shorter.',
    faction: 'ashen',
    tier: 4,
    hp: 140,
    speed: 1.35,
    armour: ArmourType.Mail,
    damage: 18,
    dmgType: DamageType.Arcane,
    range: 4.5,
    attacksPerSec: 0.5,
    splash: 0.9,
    flags: UF.Caster,
    cost: { iron: 40, souls: 60 },
    pop: 2,
    companySize: 4,
    threat: 44,
    ability: 'tollbell',
    vis: { build: 'caster', weapon: 'staff', helm: 5, pauldron: 1, cloak: true, banner: true },
  }),
  /**
   * The Crown's two wings, and the reason they are the Crown's.
   *
   * The empire kept the last horses in the realm and made a whole company out
   * of saying so (`ashen_outrider`). It kept the last *mews* as well, and there
   * is no version of this setting where an administration that files a form for
   * everything would let a resource like that lapse — it would simply have a
   * clerk for it. So the humans fly, and they fly on requisition.
   *
   * The Ashmare is the cheap half: a light horse that will not put a foot down
   * on falling ash, which the riders have had two generations to reframe as
   * training. It is priced against the Grey Outriders almost exactly — both
   * establishments come out of the same four-hundred-point rule — and buys the
   * same threat in twelve models rather than fifteen, over ground that is not
   * there.
   */
  u({
    id: 'ashen_pegasus',
    name: 'Ashmare Riders',
    flavour: 'The mares will not set a hoof in falling ash. Two generations of riders have called that training.',
    faction: 'ashen',
    tier: 2,
    hp: 150,
    speed: 3.6,
    radius: 0.34,
    armour: ArmourType.Mail,
    damage: 17,
    dmgType: DamageType.Pierce,
    attacksPerSec: 0.8,
    windup: 0.45,
    flags: UF.Flying | UF.Cavalry,
    cost: { timber: 24, iron: 30, grain: 62 },
    pop: 2,
    companySize: 12,
    threat: 34,
    stamina: 1.1,
    ability: 'charge',
    // Authored, and this is the case the derivation cannot make: `deriveVoice`
    // would hear plate and mail before it heard the animal carrying them. What
    // is struck when a lance goes into a flying horse is the horse.
    voice: 'beast',
    vis: { build: 'flier', weapon: 'spear', helm: 1, pauldron: 1, cloak: true, scale: 1.05 },
  }),
  u({
    id: 'ashen_gryphon',
    tech: 'war_heavy_horse',
    name: 'Gryphons of the Broken Perch',
    flavour: 'Eleven perches were cut into the cliff above Emberhold. The Crown does not publish how many are still answered.',
    faction: 'ashen',
    tier: 3,
    hp: 340,
    speed: 3.2,
    radius: 0.44,
    armour: ArmourType.Plate,
    damage: 36,
    dmgType: DamageType.Slash,
    attacksPerSec: 0.72,
    windup: 0.45,
    splash: 0.5,
    flags: UF.Flying | UF.Cavalry | UF.Large,
    cost: { iron: 95, grain: 85, souls: 25 },
    pop: 3,
    companySize: 5,
    threat: 66,
    ability: 'charge',
    voice: 'beast',
    vis: { build: 'flier', weapon: 'sword', helm: 4, pauldron: 2, cloak: true, banner: true, scale: 1.3 },
  }),

  // ── Karrun-Dûr ───────────────────────────────────────────────────────────
  u({
    id: 'karrun_shieldbearer',
    name: 'Karrun Shieldbearers',
    flavour: 'A door with opinions.',
    faction: 'karrun',
    hp: 140,
    speed: 1.15,
    armour: ArmourType.Shielded,
    damage: 10,
    dmgType: DamageType.Blunt,
    attacksPerSec: 0.75,
    flags: UF.Shielded,
    cost: { stone: 20, iron: 20, grain: 30 },
    companySize: 27,
    threat: 15,
    ability: 'anchor',
    vis: { build: 'infantry', weapon: 'axe', shield: true, helm: 3, pauldron: 2, scale: 0.9 },
  }),
  u({
    id: 'karrun_pikeman',
    name: 'Deeppike Line',
    flavour: 'Eighteen feet of ash and a very old grudge.',
    faction: 'karrun',
    hp: 120,
    speed: 1.1,
    armour: ArmourType.Mail,
    damage: 16,
    dmgType: DamageType.Pierce,
    range: 1.15,
    attacksPerSec: 0.6,
    windup: 0.55,
    flags: UF.Spear,
    cost: { timber: 30, iron: 24, grain: 30 },
    companySize: 22,
    threat: 18,
    ability: 'brace',
    vis: { build: 'infantry', weapon: 'spear', helm: 2, pauldron: 2, scale: 0.9 },
  }),
  u({
    id: 'karrun_handgonner',
    name: 'Handgonners',
    flavour: 'Loud, slow, and unkind to heirlooms.',
    faction: 'karrun',
    hp: 84,
    speed: 1.15,
    armour: ArmourType.Mail,
    damage: 30,
    dmgType: DamageType.Pierce,
    range: 5.6,
    attacksPerSec: 0.33,
    windup: 0.6,
    flags: UF.Ranged,
    cost: { iron: 45, stone: 10, grain: 25 },
    companySize: 15,
    threat: 26,
    vis: { build: 'archer', weapon: 'gun', helm: 3, pauldron: 2, scale: 0.9 },
  }),
  u({
    id: 'karrun_forgeguard',
    name: 'Forgeguard',
    flavour: 'They quench the blades in the same trough they wash in. It saves a trip.',
    faction: 'karrun',
    tier: 2,
    hp: 240,
    speed: 1.05,
    armour: ArmourType.Plate,
    damage: 28,
    dmgType: DamageType.Fire,
    attacksPerSec: 0.62,
    flags: UF.Shielded,
    cost: { iron: 70, stone: 30, grain: 45 },
    pop: 2,
    companySize: 10,
    threat: 40,
    vis: { build: 'infantry', weapon: 'hammer', helm: 4, pauldron: 2, scale: 1.0 },
  }),
  u({
    id: 'karrun_ballistier',
    tech: 'war_arbalests',
    name: 'Ballistiers',
    flavour: 'Crew of four. Three to aim, one to argue.',
    faction: 'karrun',
    tier: 3,
    hp: 190,
    speed: 0.75,
    radius: 0.42,
    armour: ArmourType.Structure,
    damage: 62,
    dmgType: DamageType.Siege,
    range: 9.5,
    attacksPerSec: 0.22,
    windup: 0.66,
    splash: 0.8,
    flags: UF.Siege | UF.Ranged,
    cost: { timber: 60, iron: 60, stone: 20 },
    pop: 3,
    companySize: 4,
    threat: 48,
    vis: { build: 'siege', weapon: 'none', scale: 1.2, pauldron: 0 },
  }),
  u({
    id: 'karrun_runesmith',
    name: 'Runesmith',
    flavour: 'Writes on stone in a language stone agrees with.',
    faction: 'karrun',
    tier: 3,
    hp: 130,
    speed: 1.1,
    armour: ArmourType.Mail,
    damage: 20,
    dmgType: DamageType.Arcane,
    range: 4.2,
    attacksPerSec: 0.5,
    flags: UF.Caster,
    cost: { iron: 50, souls: 40 },
    pop: 2,
    companySize: 4,
    threat: 38,
    ability: 'wardstone',
    vis: { build: 'caster', weapon: 'hammer', helm: 3, pauldron: 2, scale: 0.92 },
  }),
  u({
    id: 'karrun_deepbreaker',
    name: 'Deepbreaker',
    flavour: 'It was a mining engine. The modifications were minimal, and regrettable.',
    faction: 'karrun',
    tier: 4,
    hp: 620,
    speed: 0.85,
    radius: 0.6,
    armour: ArmourType.Structure,
    damage: 70,
    dmgType: DamageType.Siege,
    attacksPerSec: 0.4,
    splash: 1.2,
    flags: UF.Siege | UF.Large,
    cost: { iron: 140, stone: 90, souls: 60 },
    pop: 5,
    companySize: 2,
    threat: 95,
    vis: { build: 'siege', weapon: 'hammer', scale: 1.5, pauldron: 2 },
  }),
  /**
   * Karrun-Dûr do not keep beasts, and both of these are machines.
   *
   * A people who went *down* to meet the dark do not have a mews and would not
   * trust one. What they have is a works, so their two wings are wound and
   * bolted: a hand-cranked gyre with a rifle in it, and a thing that is
   * honestly a bombard which has been persuaded not to land. Both take `voice`
   * from timber and iron rather than from anything with a pulse — the Gyrecraft
   * authors it, because `deriveVoice` hears the flag before the frame and would
   * have called a spring-wound crate a spirit.
   */
  u({
    id: 'karrun_gyre',
    name: 'Gyrecraft',
    flavour: 'Spring-wound, hand-cranked, and good for four minutes. The fifth minute is the pilot’s own arrangement.',
    faction: 'karrun',
    tier: 2,
    hp: 130,
    speed: 2.6,
    radius: 0.34,
    armour: ArmourType.Structure,
    damage: 26,
    dmgType: DamageType.Pierce,
    range: 5.4,
    attacksPerSec: 0.42,
    windup: 0.55,
    flags: UF.Flying | UF.Ranged,
    cost: { iron: 62, stone: 14, grain: 24 },
    pop: 2,
    companySize: 11,
    threat: 36,
    voice: 'timber',
    vis: { build: 'flier', weapon: 'gun', helm: 3, pauldron: 2, scale: 0.92 },
  }),
  u({
    id: 'karrun_ironkite',
    name: 'Ironkite',
    flavour: 'It does not so much fly as decline, at length, to land. The bombs are the argument.',
    faction: 'karrun',
    tier: 4,
    hp: 620,
    speed: 1.9,
    radius: 0.55,
    armour: ArmourType.Structure,
    damage: 88,
    dmgType: DamageType.Siege,
    range: 6.5,
    attacksPerSec: 0.3,
    windup: 0.62,
    splash: 1.4,
    flags: UF.Flying | UF.Ranged | UF.Siege | UF.Large,
    cost: { iron: 165, stone: 70, souls: 50 },
    pop: 5,
    companySize: 2,
    threat: 118,
    vis: { build: 'flier', weapon: 'none', scale: 1.5, pauldron: 0, banner: true },
  }),

  // ── The Verdant Choir ────────────────────────────────────────────────────
  u({
    id: 'verdant_glaive',
    name: 'Glaivesingers',
    flavour: 'The blade is a note. The note is held rather a long time.',
    faction: 'verdant',
    hp: 82,
    speed: 1.7,
    armour: ArmourType.Leather,
    damage: 13,
    dmgType: DamageType.Slash,
    range: 0.85,
    attacksPerSec: 0.9,
    cost: { timber: 28, grain: 26 },
    companySize: 31,
    threat: 13,
    vis: { build: 'infantry', weapon: 'scythe', helm: 0, pauldron: 0, cloak: true, scale: 1.02 },
  }),
  u({
    id: 'verdant_longbow',
    name: 'Choir Longbows',
    flavour: 'They have been practising since before the sky went grey. It shows.',
    faction: 'verdant',
    hp: 58,
    speed: 1.65,
    armour: ArmourType.Unarmoured,
    damage: 12,
    dmgType: DamageType.Arrow,
    range: 8.0,
    attacksPerSec: 0.66,
    windup: 0.58,
    flags: UF.Ranged,
    cost: { timber: 36, grain: 22 },
    companySize: 22,
    threat: 18,
    vis: { build: 'archer', weapon: 'bow', helm: 0, pauldron: 0, cloak: true },
  }),
  u({
    id: 'verdant_thornguard',
    name: 'Thornguard',
    flavour: 'Bark over mail. It grows back; the mail does not.',
    faction: 'verdant',
    tier: 2,
    hp: 175,
    speed: 1.25,
    armour: ArmourType.Shielded,
    damage: 14,
    dmgType: DamageType.Pierce,
    attacksPerSec: 0.8,
    flags: UF.Shielded,
    cost: { timber: 55, grain: 35 },
    companySize: 17,
    threat: 24,
    ability: 'thorns',
    vis: { build: 'infantry', weapon: 'spear', shield: true, helm: 2, pauldron: 2 },
  }),
  u({
    id: 'verdant_stagrider',
    tech: 'war_horsemastery',
    name: 'Stagriders',
    flavour: 'The stags volunteered. That is the part nobody likes.',
    faction: 'verdant',
    tier: 2,
    hp: 150,
    speed: 3.4,
    radius: 0.36,
    armour: ArmourType.Leather,
    damage: 15,
    dmgType: DamageType.Pierce,
    attacksPerSec: 0.75,
    flags: UF.Cavalry,
    cost: { timber: 55, grain: 55 },
    pop: 2,
    companySize: 14,
    threat: 28,
    ability: 'charge',
    vis: { build: 'cavalry', weapon: 'spear', helm: 0, pauldron: 1, cloak: true },
  }),
  u({
    id: 'verdant_songweaver',
    name: 'Songweaver',
    flavour: 'Asks the ground for a favour. The ground remembers who planted it.',
    faction: 'verdant',
    tier: 3,
    hp: 110,
    speed: 1.4,
    armour: ArmourType.Unarmoured,
    damage: 16,
    dmgType: DamageType.Arcane,
    range: 5.5,
    attacksPerSec: 0.55,
    flags: UF.Caster,
    cost: { timber: 40, souls: 45 },
    pop: 2,
    companySize: 4,
    threat: 36,
    ability: 'entangle',
    vis: { build: 'caster', weapon: 'staff', helm: 0, pauldron: 0, cloak: true },
  }),
  u({
    id: 'verdant_bramblewarden',
    name: 'Bramblewarden',
    flavour: 'Nine feet of grievance with a root system.',
    faction: 'verdant',
    tier: 4,
    hp: 520,
    speed: 1.0,
    radius: 0.55,
    armour: ArmourType.Monstrous,
    damage: 44,
    dmgType: DamageType.Blunt,
    attacksPerSec: 0.5,
    splash: 0.9,
    flags: UF.Large,
    cost: { timber: 120, souls: 70 },
    pop: 4,
    companySize: 3,
    threat: 82,
    ability: 'thorns',
    vis: { build: 'brute', weapon: 'claw', scale: 1.45, pauldron: 2 },
  }),
  u({
    id: 'verdant_owlrider',
    name: 'The Silent Wing',
    flavour: 'You are told they are overhead by the fact that the wood has stopped saying anything.',
    faction: 'verdant',
    tier: 2,
    hp: 128,
    speed: 3.5,
    radius: 0.3,
    armour: ArmourType.Leather,
    damage: 13,
    dmgType: DamageType.Arrow,
    range: 6.2,
    attacksPerSec: 0.62,
    windup: 0.55,
    flags: UF.Flying | UF.Ranged,
    cost: { timber: 48, grain: 42 },
    pop: 2,
    companySize: 13,
    threat: 32,
    voice: 'beast',
    vis: { build: 'flier', weapon: 'bow', helm: 0, pauldron: 0, cloak: true, scale: 1.0 },
  }),
  u({
    id: 'verdant_thornwyrm',
    name: 'Thornwyrm',
    flavour: 'Grown rather than hatched. The ring it was grown from is nine trees short and does not discuss it.',
    faction: 'verdant',
    tier: 4,
    hp: 560,
    speed: 2.2,
    radius: 0.56,
    armour: ArmourType.Monstrous,
    damage: 54,
    dmgType: DamageType.Pierce,
    attacksPerSec: 0.5,
    splash: 0.9,
    flags: UF.Flying | UF.Large,
    cost: { timber: 130, souls: 85 },
    pop: 4,
    companySize: 2,
    threat: 112,
    ability: 'thorns',
    vis: { build: 'flier', weapon: 'claw', scale: 1.5, pauldron: 2 },
  }),

  // ── The Pale Litany ──────────────────────────────────────────────────────
  u({
    id: 'litany_wretch',
    name: 'Wretches',
    flavour: 'Raised without ceremony, which they seem to resent.',
    faction: 'litany',
    hp: 55,
    speed: 1.3,
    armour: ArmourType.Unarmoured,
    damage: 7,
    dmgType: DamageType.Slash,
    attacksPerSec: 0.8,
    flags: UF.Undead,
    cost: { souls: 8 },
    pop: 0,
    companySize: 80,
    threat: 5,
    vis: { build: 'swarm', weapon: 'claw', helm: 0, pauldron: 0, scale: 0.9 },
  }),
  u({
    id: 'litany_boneshield',
    name: 'Boneshield Ranks',
    flavour: 'The shields are also them. Economy in all things.',
    faction: 'litany',
    hp: 105,
    speed: 1.25,
    armour: ArmourType.Shielded,
    damage: 9,
    dmgType: DamageType.Blunt,
    attacksPerSec: 0.75,
    flags: UF.Undead | UF.Shielded,
    cost: { souls: 22, iron: 10 },
    companySize: 36,
    threat: 11,
    vis: { build: 'infantry', weapon: 'sword', shield: true, helm: 1, pauldron: 1, scale: 0.95 },
  }),
  u({
    id: 'litany_gravebow',
    name: 'Gravebows',
    flavour: 'They do not blink, which is unnerving, and do not miss, which is worse.',
    faction: 'litany',
    hp: 60,
    speed: 1.2,
    armour: ArmourType.Unarmoured,
    damage: 10,
    dmgType: DamageType.Arrow,
    range: 6.6,
    attacksPerSec: 0.6,
    flags: UF.Undead | UF.Ranged,
    cost: { souls: 26, timber: 14 },
    companySize: 29,
    threat: 14,
    vis: { build: 'archer', weapon: 'bow', helm: 1, pauldron: 0, scale: 0.95 },
  }),
  u({
    id: 'litany_cryptknight',
    name: 'Crypt Knights',
    flavour: 'Interred in armour, which turned out to be a promotion.',
    faction: 'litany',
    tier: 2,
    hp: 230,
    speed: 1.3,
    armour: ArmourType.Plate,
    damage: 26,
    dmgType: DamageType.Slash,
    attacksPerSec: 0.7,
    flags: UF.Undead,
    cost: { souls: 55, iron: 35 },
    pop: 2,
    companySize: 12,
    threat: 34,
    vis: { build: 'infantry', weapon: 'sword', shield: true, helm: 4, pauldron: 2, cloak: true, scale: 1.08 },
  }),
  u({
    id: 'litany_palepriest',
    name: 'Pale Priest',
    flavour: 'Reads the last rites forwards. It is the backwards recital that does the work.',
    faction: 'litany',
    tier: 3,
    hp: 115,
    speed: 1.2,
    armour: ArmourType.Unarmoured,
    damage: 15,
    dmgType: DamageType.Arcane,
    range: 5.0,
    attacksPerSec: 0.5,
    flags: UF.Caster | UF.Undead,
    cost: { souls: 80 },
    pop: 2,
    companySize: 4,
    threat: 42,
    ability: 'reanimate',
    vis: { build: 'caster', weapon: 'staff', helm: 5, pauldron: 0, cloak: true },
  }),
  u({
    id: 'litany_charnelox',
    name: 'Charnel Ox',
    flavour: 'Six oxen, once. The arithmetic was performed by a specialist.',
    faction: 'litany',
    tier: 4,
    hp: 560,
    speed: 1.05,
    radius: 0.58,
    armour: ArmourType.Monstrous,
    damage: 48,
    dmgType: DamageType.Blunt,
    attacksPerSec: 0.45,
    splash: 1.0,
    // Champion: this is the wave-50 Siege boss. Without the flag the director
    // treats it as an ordinary body — no priority arrival, no name card, and it
    // can be routed off the field by fear like any other conscript.
    flags: UF.Undead | UF.Large | UF.Champion,
    cost: { souls: 140, iron: 40 },
    pop: 4,
    companySize: 2,
    threat: 86,
    vis: { build: 'brute', weapon: 'claw', scale: 1.5, pauldron: 2 },
  }),
  /**
   * The Court's two wings are both `UF.Undead`, so both are `bone` whatever
   * else they are — `deriveVoice` puts the risen first and `tests/voices.test.ts`
   * holds it. That is the right answer and not a concession: a flock assembled
   * out of the smaller drawers of an ossuary does not sound like a bird.
   */
  u({
    id: 'litany_ossuaryflock',
    name: 'Ossuary Flock',
    flavour: 'Reassembled from the smaller drawers. Nothing in it was ever a bird, and all of it flies.',
    faction: 'litany',
    tier: 2,
    hp: 78,
    speed: 3.2,
    radius: 0.24,
    armour: ArmourType.Unarmoured,
    damage: 11,
    dmgType: DamageType.Slash,
    attacksPerSec: 1.15,
    flags: UF.Undead | UF.Flying,
    cost: { souls: 40, iron: 8 },
    pop: 1,
    companySize: 20,
    threat: 20,
    vis: { build: 'flier', weapon: 'claw', helm: 1, pauldron: 0, scale: 0.82 },
  }),
  u({
    id: 'litany_barrowwyrm',
    name: 'The Barrow-Wyrm',
    flavour: 'Interred in three counties. Exhumed, on the Court’s instruction, as one.',
    faction: 'litany',
    tier: 4,
    hp: 720,
    speed: 2.0,
    radius: 0.6,
    armour: ArmourType.Monstrous,
    damage: 62,
    dmgType: DamageType.Arcane,
    attacksPerSec: 0.45,
    splash: 1.2,
    flags: UF.Undead | UF.Flying | UF.Large,
    cost: { souls: 190, iron: 50 },
    pop: 4,
    companySize: 2,
    threat: 120,
    vis: { build: 'flier', weapon: 'claw', cloak: true, scale: 1.55, pauldron: 2 },
  }),

  // ── The Moonbound ────────────────────────────────────────────────────────
  u({
    id: 'moon_pack',
    name: 'Packbrothers',
    flavour: 'They count the dead by smell, and are seldom wrong.',
    faction: 'moonbound',
    hp: 92,
    speed: 2.0,
    armour: ArmourType.Leather,
    damage: 11,
    dmgType: DamageType.Slash,
    attacksPerSec: 1.0,
    cost: { grain: 34, timber: 14 },
    companySize: 33,
    threat: 12,
    vis: { build: 'infantry', weapon: 'axe', helm: 0, pauldron: 1, scale: 1.02 },
  }),
  u({
    id: 'moon_howler',
    name: 'Howlers',
    flavour: 'The howl is not for you. You are simply within earshot.',
    faction: 'moonbound',
    tier: 2,
    hp: 130,
    speed: 2.2,
    armour: ArmourType.Leather,
    damage: 18,
    dmgType: DamageType.Slash,
    attacksPerSec: 0.9,
    cost: { grain: 50, iron: 20 },
    companySize: 17,
    threat: 24,
    ability: 'warcry',
    vis: { build: 'infantry', weapon: 'claw', helm: 1, pauldron: 2, scale: 1.1 },
  }),
  u({
    id: 'moon_slinger',
    name: 'Moonslingers',
    flavour: 'A stone, a strap, and an unreasonable amount of practice.',
    faction: 'moonbound',
    hp: 64,
    speed: 1.9,
    armour: ArmourType.Unarmoured,
    damage: 10,
    dmgType: DamageType.Blunt,
    range: 5.4,
    attacksPerSec: 0.7,
    flags: UF.Ranged,
    cost: { timber: 22, grain: 22 },
    companySize: 33,
    threat: 12,
    vis: { build: 'archer', weapon: 'none', helm: 0, pauldron: 0 },
  }),
  u({
    id: 'moon_shadowclaw',
    name: 'Shadowclaws',
    flavour: 'Arrive before the sound of arriving.',
    faction: 'moonbound',
    tier: 3,
    hp: 145,
    speed: 3.6,
    radius: 0.3,
    armour: ArmourType.Leather,
    damage: 30,
    dmgType: DamageType.Pierce,
    attacksPerSec: 1.1,
    cost: { grain: 60, souls: 30 },
    pop: 2,
    companySize: 10,
    threat: 40,
    ability: 'flank',
    vis: { build: 'infantry', weapon: 'claw', helm: 2, pauldron: 1, cloak: true },
  }),
  u({
    id: 'moon_alpha',
    name: 'Alpha of the Long Howl',
    flavour: 'Leads from the front because there is no other direction it understands.',
    faction: 'moonbound',
    tier: 4,
    hp: 420,
    speed: 2.6,
    radius: 0.46,
    armour: ArmourType.Mail,
    damage: 52,
    dmgType: DamageType.Slash,
    attacksPerSec: 0.85,
    splash: 0.7,
    flags: UF.Large,
    cost: { grain: 90, iron: 60, souls: 60 },
    pop: 4,
    companySize: 2,
    threat: 84,
    ability: 'warcry',
    vis: { build: 'champion', weapon: 'axe', helm: 3, pauldron: 2, cloak: true, scale: 1.35 },
  }),
  u({
    id: 'moon_moonsinger',
    name: 'Moonsinger',
    flavour: 'Sings the moon down where it can be useful.',
    faction: 'moonbound',
    tier: 3,
    hp: 108,
    speed: 1.6,
    armour: ArmourType.Unarmoured,
    damage: 17,
    dmgType: DamageType.Arcane,
    range: 4.8,
    attacksPerSec: 0.55,
    flags: UF.Caster,
    cost: { souls: 55, grain: 30 },
    pop: 2,
    companySize: 4,
    threat: 34,
    ability: 'nightveil',
    vis: { build: 'caster', weapon: 'staff', helm: 0, pauldron: 0, cloak: true },
  }),
  u({
    id: 'moon_stormraven',
    name: 'Storm-Ravens',
    flavour: 'They ride what the pack cannot follow. The pack has not forgiven them for it and does not intend to.',
    faction: 'moonbound',
    tier: 2,
    hp: 118,
    speed: 3.8,
    radius: 0.3,
    armour: ArmourType.Leather,
    damage: 16,
    dmgType: DamageType.Slash,
    attacksPerSec: 0.95,
    flags: UF.Flying,
    cost: { grain: 55, timber: 22, souls: 12 },
    pop: 2,
    companySize: 13,
    threat: 31,
    voice: 'beast',
    vis: { build: 'flier', weapon: 'claw', helm: 1, pauldron: 1, cloak: true, scale: 0.98 },
  }),
  u({
    id: 'moon_wintering',
    name: 'The Wintering Wing',
    flavour: 'Comes down out of the weather with the weather still on it.',
    faction: 'moonbound',
    tier: 4,
    hp: 480,
    speed: 2.9,
    radius: 0.5,
    armour: ArmourType.Mail,
    damage: 60,
    dmgType: DamageType.Slash,
    attacksPerSec: 0.8,
    splash: 0.8,
    flags: UF.Flying | UF.Large,
    cost: { grain: 95, iron: 70, souls: 70 },
    pop: 4,
    companySize: 2,
    threat: 116,
    ability: 'warcry',
    vis: { build: 'flier', weapon: 'axe', helm: 3, pauldron: 2, cloak: true, scale: 1.42 },
  }),

  // ── The Gilded Vigil ─────────────────────────────────────────────────────
  u({
    id: 'vigil_lampbearer',
    name: 'Lampbearers',
    flavour: 'Carry a light into places that have opinions about light.',
    faction: 'vigil',
    hp: 155,
    speed: 1.35,
    armour: ArmourType.Shielded,
    damage: 16,
    dmgType: DamageType.Fire,
    attacksPerSec: 0.8,
    flags: UF.Shielded,
    cost: { iron: 45, grain: 40, souls: 15 },
    pop: 2,
    companySize: 17,
    threat: 24,
    vis: { build: 'infantry', weapon: 'hammer', shield: true, helm: 3, pauldron: 2, cloak: true, scale: 1.06 },
  }),
  u({
    id: 'vigil_censer',
    name: 'Censer-Bearers',
    flavour: 'The smoke is a prayer. It is also, mostly, lead.',
    faction: 'vigil',
    tier: 2,
    hp: 130,
    speed: 1.3,
    armour: ArmourType.Mail,
    damage: 20,
    dmgType: DamageType.Fire,
    range: 1.3,
    attacksPerSec: 0.7,
    splash: 1.1,
    cost: { iron: 40, souls: 35 },
    pop: 2,
    companySize: 13,
    threat: 30,
    vis: { build: 'infantry', weapon: 'hammer', helm: 5, pauldron: 1, cloak: true },
  }),
  u({
    id: 'vigil_pyreknight',
    name: 'Pyre Knights',
    flavour: 'Armour polished to a mirror, so the dark has to look at itself.',
    faction: 'vigil',
    tier: 3,
    hp: 340,
    speed: 2.8,
    radius: 0.4,
    armour: ArmourType.Plate,
    damage: 31,
    dmgType: DamageType.Fire,
    attacksPerSec: 0.72,
    flags: UF.Cavalry,
    cost: { iron: 90, souls: 70, grain: 60 },
    pop: 3,
    companySize: 7,
    threat: 60,
    ability: 'charge',
    vis: { build: 'cavalry', weapon: 'sword', helm: 4, pauldron: 2, cloak: true, banner: true, scale: 1.18 },
  }),
  u({
    id: 'vigil_sunbolt',
    name: 'Sunbolt Sisters',
    flavour: 'They aim by faith and correct by arithmetic.',
    faction: 'vigil',
    hp: 74,
    speed: 1.4,
    armour: ArmourType.Leather,
    damage: 16,
    dmgType: DamageType.Fire,
    range: 6.8,
    attacksPerSec: 0.55,
    flags: UF.Ranged,
    cost: { iron: 35, souls: 25 },
    pop: 2,
    companySize: 17,
    threat: 24,
    vis: { build: 'archer', weapon: 'crossbow', helm: 5, pauldron: 1, cloak: true },
  }),
  u({
    id: 'vigil_confessor',
    name: 'Confessor',
    flavour: 'Will hear anything. Forgives almost none of it.',
    faction: 'vigil',
    tier: 3,
    hp: 145,
    speed: 1.3,
    armour: ArmourType.Mail,
    damage: 22,
    dmgType: DamageType.Arcane,
    range: 4.6,
    attacksPerSec: 0.5,
    flags: UF.Caster,
    cost: { souls: 90 },
    pop: 2,
    companySize: 4,
    threat: 46,
    ability: 'sanctify',
    vis: { build: 'caster', weapon: 'staff', helm: 5, pauldron: 1, cloak: true, banner: true },
  }),
  u({
    id: 'vigil_reliquar',
    name: 'The Reliquar',
    flavour: 'A walking shrine. The saint inside has not been consulted in some time.',
    faction: 'vigil',
    tier: 4,
    hp: 640,
    speed: 0.95,
    radius: 0.58,
    armour: ArmourType.Plate,
    damage: 46,
    dmgType: DamageType.Fire,
    attacksPerSec: 0.5,
    splash: 1.3,
    flags: UF.Large | UF.SpellEater,
    cost: { iron: 140, souls: 160 },
    pop: 5,
    companySize: 2,
    threat: 102,
    ability: 'sanctify',
    vis: { build: 'brute', weapon: 'hammer', scale: 1.5, pauldron: 2, banner: true },
  }),
  /**
   * The second human roster flies too, and it flies differently.
   *
   * The owner's point was that humans get gryphon riders; the Vigil is the case
   * that makes the point interesting rather than repeated. The Ashen mews is a
   * requisition and its wings are lances and cavalry sabres. The Vigil's is a
   * *reliquary*: a bird on a chain and, on the chain below the bird, the
   * censer. Same flag, same premium, all of it Fire and splash instead of a
   * charge, and four models to a company rather than five because the Gilded
   * Vigil's population cap is four tenths lower than anybody's and every
   * soldier it fields is a relic it cannot replace cheaply.
   */
  u({
    id: 'vigil_censerhawk',
    name: 'Censer-Hawks',
    flavour: 'A hawk on a chain, and on the chain below the hawk, the censer. The smoke goes where the bird goes.',
    faction: 'vigil',
    tier: 2,
    hp: 120,
    speed: 3.4,
    radius: 0.3,
    armour: ArmourType.Mail,
    damage: 19,
    dmgType: DamageType.Fire,
    range: 1.2,
    attacksPerSec: 0.75,
    splash: 0.8,
    flags: UF.Flying,
    cost: { iron: 40, souls: 40, grain: 30 },
    pop: 2,
    companySize: 12,
    threat: 33,
    voice: 'beast',
    vis: { build: 'flier', weapon: 'hammer', helm: 5, pauldron: 1, cloak: true, scale: 0.95 },
  }),
  u({
    id: 'vigil_gryphon',
    tech: 'war_heavy_horse',
    name: 'Gryphons of the Ninth Oath',
    flavour: 'The ninth oath was sworn to the bird as well. Nobody has ever established whether the bird was listening.',
    faction: 'vigil',
    tier: 3,
    hp: 370,
    speed: 3.0,
    radius: 0.44,
    armour: ArmourType.Plate,
    damage: 34,
    dmgType: DamageType.Fire,
    attacksPerSec: 0.7,
    splash: 0.7,
    flags: UF.Flying | UF.Cavalry | UF.Large,
    cost: { iron: 100, souls: 90, grain: 60 },
    pop: 3,
    companySize: 4,
    threat: 72,
    voice: 'beast',
    vis: { build: 'flier', weapon: 'sword', helm: 5, pauldron: 2, cloak: true, banner: true, scale: 1.32 },
  }),

  // ── The Ironfang Horde (invader) ─────────────────────────────────────────
  u({
    id: 'iron_grunt',
    name: 'Fang Grunt',
    flavour: 'Issued a weapon on the understanding it would be returned, eventually, by someone.',
    faction: 'ironfang',
    hp: 105,
    speed: 1.55,
    armour: ArmourType.Leather,
    damage: 11,
    dmgType: DamageType.Slash,
    attacksPerSec: 0.85,
    companySize: 40,
    threat: 10,
    vis: { build: 'infantry', weapon: 'axe', helm: 1, pauldron: 2, scale: 1.05 },
  }),
  u({
    id: 'iron_archer',
    name: 'Fang Bowyer',
    flavour: 'Fletches with whatever was nearest and least attached.',
    faction: 'ironfang',
    hp: 62,
    speed: 1.5,
    armour: ArmourType.Unarmoured,
    damage: 8,
    dmgType: DamageType.Arrow,
    range: 5.8,
    attacksPerSec: 0.7,
    flags: UF.Ranged,
    companySize: 36,
    threat: 11,
    vis: { build: 'archer', weapon: 'bow', helm: 0, pauldron: 1 },
  }),
  u({
    id: 'iron_shieldbreaker',
    name: 'Shieldbreaker',
    flavour: 'Specialist. The speciality is other people’s shields.',
    faction: 'ironfang',
    tier: 2,
    hp: 165,
    speed: 1.45,
    armour: ArmourType.Mail,
    damage: 24,
    dmgType: DamageType.Blunt,
    attacksPerSec: 0.7,
    companySize: 15,
    threat: 26,
    ability: 'sunder',
    vis: { build: 'infantry', weapon: 'hammer', helm: 2, pauldron: 2, scale: 1.12 },
  }),
  u({
    id: 'iron_pavise',
    name: 'Pavise Line',
    flavour: 'Carries a door to hide behind. Considers this cunning, and is correct.',
    faction: 'ironfang',
    tier: 2,
    hp: 175,
    speed: 1.15,
    armour: ArmourType.Shielded,
    damage: 12,
    dmgType: DamageType.Pierce,
    attacksPerSec: 0.7,
    flags: UF.Shielded,
    companySize: 18,
    threat: 22,
    ability: 'anchor',
    vis: { build: 'infantry', weapon: 'spear', shield: true, helm: 2, pauldron: 2, scale: 1.05 },
  }),
  u({
    id: 'iron_wolfrider',
    name: 'Wolfrider',
    flavour: 'Neither of them is in charge, and both are enjoying it.',
    faction: 'ironfang',
    tier: 2,
    hp: 135,
    speed: 3.5,
    radius: 0.34,
    armour: ArmourType.Leather,
    damage: 12,
    dmgType: DamageType.Slash,
    attacksPerSec: 0.9,
    flags: UF.Cavalry,
    companySize: 17,
    threat: 24,
    ability: 'charge',
    vis: { build: 'cavalry', weapon: 'axe', helm: 0, pauldron: 1 },
  }),
  u({
    id: 'iron_sapper',
    name: 'Sapper',
    flavour: 'Brought a bag. Will not say what is in the bag.',
    faction: 'ironfang',
    tier: 2,
    hp: 78,
    speed: 1.8,
    armour: ArmourType.Unarmoured,
    damage: 46,
    dmgType: DamageType.Siege,
    attacksPerSec: 0.45,
    splash: 0.9,
    flags: UF.Sapper,
    companySize: 18,
    threat: 22,
    ability: 'sap',
    vis: { build: 'infantry', weapon: 'none', helm: 0, pauldron: 0, scale: 0.95 },
  }),
  u({
    id: 'iron_ogre',
    name: 'Ogre',
    flavour: 'Was told which end of the settlement to start at. Has forgotten, and will improvise.',
    faction: 'ironfang',
    tier: 3,
    hp: 560,
    speed: 1.25,
    radius: 0.56,
    armour: ArmourType.Monstrous,
    damage: 44,
    dmgType: DamageType.Blunt,
    attacksPerSec: 0.5,
    splash: 1.0,
    flags: UF.Large,
    companySize: 3,
    threat: 62,
    ability: 'warcry',
    vis: { build: 'brute', weapon: 'hammer', scale: 1.5, pauldron: 2, helm: 1 },
  }),
  u({
    id: 'iron_ram',
    name: 'Gate-Ram',
    flavour: 'Twelve of them push. One of them steers. Nobody has ever seen it turn.',
    faction: 'ironfang',
    tier: 3,
    hp: 980,
    speed: 0.72,
    radius: 0.7,
    armour: ArmourType.Structure,
    damage: 110,
    dmgType: DamageType.Siege,
    attacksPerSec: 0.3,
    windup: 0.6,
    flags: UF.Siege | UF.Large,
    companySize: 1,
    threat: 90,
    ability: 'ramfocus',
    vis: { build: 'siege', weapon: 'none', scale: 1.7, pauldron: 0 },
  }),
  u({
    id: 'iron_warlord',
    name: 'Ironfang Warlord',
    flavour: 'Has killed every rival, and now has to think of something else to do.',
    faction: 'ironfang',
    tier: 4,
    hp: 1400,
    speed: 1.6,
    radius: 0.62,
    armour: ArmourType.Plate,
    damage: 84,
    dmgType: DamageType.Slash,
    attacksPerSec: 0.7,
    splash: 1.1,
    flags: UF.Champion | UF.Large,
    companySize: 1,
    threat: 200,
    ability: 'warcry',
    vis: { build: 'champion', weapon: 'axe', helm: 4, pauldron: 2, cloak: true, banner: true, scale: 1.6 },
  }),
  /**
   * One wyvern per invader roster and no more, and the restraint is mechanical
   * rather than editorial.
   *
   * `pickStructureTarget` returns immediately for anything flying, so an
   * invading flier cannot take a wall or a building as a target at all: it is a
   * pure anti-unit weapon that will kill your archers, your casters and your
   * villagers and cannot knock one plank off your Hearth. A horde weighted
   * towards fliers therefore does not besiege — it circles until
   * `SIEGE_BREAKING_POINT` sends it home. One drawable flier per faction is
   * enough for the director's choke-point counter to have something to buy and
   * few enough that a wave is never mostly made of them.
   */
  u({
    id: 'iron_wyvern',
    name: 'Wyvern',
    flavour: 'Kept hungry on the march. A fed one does not come back down, and the rider goes with it.',
    faction: 'ironfang',
    tier: 3,
    hp: 600,
    speed: 2.6,
    radius: 0.54,
    armour: ArmourType.Monstrous,
    damage: 58,
    dmgType: DamageType.Pierce,
    attacksPerSec: 0.55,
    splash: 0.9,
    flags: UF.Flying | UF.Large,
    companySize: 3,
    threat: 96,
    vis: { build: 'flier', weapon: 'claw', helm: 1, pauldron: 2, scale: 1.48 },
  }),

  // ── The Gutter Congregation (invader) ────────────────────────────────────
  u({
    id: 'gutter_swarm',
    name: 'Gutter Swarm',
    flavour: 'Individually negligible. The word for many of them is not a plural, it is a weather event.',
    faction: 'gutter',
    hp: 34,
    speed: 2.1,
    radius: 0.2,
    armour: ArmourType.Unarmoured,
    damage: 5,
    dmgType: DamageType.Slash,
    attacksPerSec: 1.25,
    companySize: 100,
    threat: 4,
    vis: { build: 'swarm', weapon: 'claw', scale: 0.78, pauldron: 0 },
  }),
  u({
    id: 'gutter_slinger',
    name: 'Gutter Slinger',
    flavour: 'Throws things it found. It finds a great deal.',
    faction: 'gutter',
    hp: 40,
    speed: 2.0,
    radius: 0.21,
    armour: ArmourType.Unarmoured,
    damage: 7,
    dmgType: DamageType.Pierce,
    range: 5.0,
    attacksPerSec: 0.85,
    flags: UF.Ranged,
    companySize: 50,
    threat: 8,
    vis: { build: 'swarm', weapon: 'none', scale: 0.8, pauldron: 0 },
  }),
  u({
    id: 'gutter_tunneller',
    name: 'Tunneller',
    flavour: 'Your walls are, from below, a suggestion.',
    faction: 'gutter',
    tier: 2,
    hp: 70,
    speed: 1.9,
    armour: ArmourType.Leather,
    damage: 12,
    dmgType: DamageType.Pierce,
    attacksPerSec: 1.0,
    companySize: 22,
    threat: 18,
    ability: 'burrow',
    vis: { build: 'swarm', weapon: 'claw', scale: 0.9, pauldron: 1 },
  }),
  u({
    id: 'gutter_censerrat',
    name: 'Censer Rat',
    flavour: 'Swings the censer until something stops it, including itself.',
    faction: 'gutter',
    tier: 3,
    hp: 95,
    speed: 2.3,
    armour: ArmourType.Unarmoured,
    damage: 22,
    dmgType: DamageType.Poison,
    range: 1.1,
    attacksPerSec: 0.9,
    splash: 1.2,
    companySize: 15,
    threat: 26,
    vis: { build: 'swarm', weapon: 'hammer', scale: 0.95, pauldron: 1 },
  }),
  u({
    id: 'gutter_bloatpriest',
    name: 'Bloat-Priest',
    flavour: 'The sermon is long. The congregation is dying of it, and considers this normal.',
    faction: 'gutter',
    tier: 4,
    hp: 400,
    speed: 1.1,
    radius: 0.5,
    armour: ArmourType.Monstrous,
    damage: 30,
    dmgType: DamageType.Poison,
    range: 4.0,
    attacksPerSec: 0.45,
    splash: 1.6,
    // Champion: the wave-20 Siege boss. See the Charnel Ox.
    flags: UF.Caster | UF.Large | UF.Champion,
    companySize: 2,
    threat: 70,
    ability: 'summonswarm',
    vis: { build: 'brute', weapon: 'staff', scale: 1.3, pauldron: 1, cloak: true },
  }),
  u({
    id: 'gutter_belfry',
    name: 'Belfry Flock',
    flavour: 'They were in the bell-tower before the Congregation was. The Congregation has written this up as scripture.',
    faction: 'gutter',
    tier: 2,
    hp: 38,
    speed: 2.6,
    radius: 0.2,
    armour: ArmourType.Unarmoured,
    damage: 7,
    dmgType: DamageType.Poison,
    attacksPerSec: 1.2,
    flags: UF.Flying,
    companySize: 44,
    threat: 9,
    voice: 'beast',
    vis: { build: 'flier', weapon: 'claw', scale: 0.72, pauldron: 0 },
  }),
  /**
   * The Congregation had five entries and one of them was a boss, which made a
   * Gutter wave a carpet of swarm with nothing in it that had to be *answered*.
   * The Gnawbeast is the thing a year of committee minutes was spent feeding.
   */
  u({
    id: 'gutter_gnawbeast',
    name: 'Gnawbeast',
    flavour: 'Fed on the congregation for a year, in the dark, by a committee that met four times to agree it was worth it.',
    faction: 'gutter',
    tier: 3,
    hp: 480,
    speed: 2.0,
    radius: 0.5,
    armour: ArmourType.Monstrous,
    damage: 42,
    dmgType: DamageType.Slash,
    attacksPerSec: 0.6,
    splash: 0.9,
    flags: UF.Large,
    companySize: 3,
    threat: 60,
    vis: { build: 'brute', weapon: 'claw', scale: 1.38, pauldron: 1 },
  }),

  // ── The Rotmoot (invader) ────────────────────────────────────────────────
  u({
    id: 'rot_shambler',
    name: 'Shambler',
    flavour: 'Was a farmer. Still walks the boundary of a field that is no longer there.',
    faction: 'rotmoot',
    hp: 120,
    speed: 1.0,
    armour: ArmourType.Unarmoured,
    damage: 10,
    dmgType: DamageType.Poison,
    attacksPerSec: 0.6,
    flags: UF.Undead,
    companySize: 44,
    threat: 9,
    ability: 'burst',
    vis: { build: 'infantry', weapon: 'claw', helm: 0, pauldron: 0, scale: 0.98 },
  }),
  u({
    id: 'rot_spewer',
    name: 'Spewer',
    flavour: 'Aims high. Gravity does the unpleasant part.',
    faction: 'rotmoot',
    tier: 2,
    hp: 90,
    speed: 1.1,
    armour: ArmourType.Unarmoured,
    damage: 18,
    dmgType: DamageType.Poison,
    range: 5.2,
    attacksPerSec: 0.45,
    splash: 1.4,
    flags: UF.Ranged,
    companySize: 20,
    threat: 20,
    vis: { build: 'archer', weapon: 'none', helm: 0, pauldron: 1, scale: 1.05 },
  }),
  u({
    id: 'rot_blightbearer',
    name: 'Blightbearer',
    flavour: 'Carries the standard. The standard is also the disease.',
    faction: 'rotmoot',
    tier: 2,
    hp: 175,
    speed: 1.05,
    armour: ArmourType.Leather,
    damage: 16,
    dmgType: DamageType.Poison,
    attacksPerSec: 0.6,
    companySize: 17,
    threat: 24,
    ability: 'plagueaura',
    vis: { build: 'infantry', weapon: 'spear', helm: 0, pauldron: 1, banner: true, scale: 1.05 },
  }),
  u({
    id: 'rot_pesthulk',
    name: 'Pesthulk',
    flavour: 'Everything it ever ate is still in there, and votes.',
    faction: 'rotmoot',
    tier: 3,
    hp: 620,
    speed: 0.95,
    radius: 0.58,
    armour: ArmourType.Monstrous,
    damage: 40,
    dmgType: DamageType.Poison,
    attacksPerSec: 0.45,
    splash: 1.3,
    flags: UF.Large,
    companySize: 3,
    threat: 66,
    ability: 'burst',
    vis: { build: 'brute', weapon: 'claw', scale: 1.5, pauldron: 2 },
  }),
  u({
    id: 'rot_sporeherald',
    name: 'Spore Herald',
    flavour: 'Speaks for the moot. The moot speaks through the lungs of whoever is nearest.',
    faction: 'rotmoot',
    tier: 4,
    hp: 460,
    speed: 1.15,
    radius: 0.48,
    armour: ArmourType.Leather,
    damage: 28,
    dmgType: DamageType.Poison,
    range: 4.5,
    attacksPerSec: 0.5,
    splash: 1.8,
    // Champion: the wave-30 Siege boss. See the Charnel Ox.
    flags: UF.Caster | UF.Champion,
    companySize: 2,
    threat: 78,
    ability: 'plagueaura',
    vis: { build: 'caster', weapon: 'staff', scale: 1.25, pauldron: 1, cloak: true },
  }),
  /**
   * The Bloat Bruiser is not a new name. It is a name the code has been using
   * for two passes and the catalogue never had.
   *
   * `systems/combat.ts` documents `burst` as "the Shambler's and the Bloat
   * Bruiser's signature", and NEXT.md's write-up of the invader signatures says
   * "a killed Shambler or Bloat Bruiser pops instantly". Neither was wrong
   * about the mechanic — `burst` is wired and does exactly that — and both were
   * naming a unit that did not exist, which is the same class of quiet lie as a
   * tech gate pointing at a node that was never written. So it exists now, at
   * the tier the shape of it implies: heavier than a Shambler, an Age below a
   * Pesthulk, and packed tightly enough that a company of them chain-pops.
   */
  u({
    id: 'rot_bloatbruiser',
    name: 'Bloat Bruiser',
    flavour: 'Carried in on the shoulders of four, none of whom survived the carrying, all of whom came anyway.',
    faction: 'rotmoot',
    tier: 2,
    hp: 260,
    speed: 0.95,
    radius: 0.42,
    armour: ArmourType.Leather,
    damage: 24,
    dmgType: DamageType.Poison,
    attacksPerSec: 0.5,
    splash: 0.9,
    companySize: 13,
    threat: 30,
    ability: 'burst',
    vis: { build: 'brute', weapon: 'claw', scale: 1.2, pauldron: 1 },
  }),
  u({
    id: 'rot_carrionchoir',
    name: 'Carrion Choir',
    flavour: 'The moot votes by weight. By evening every one of them is very heavy and the motion carries.',
    faction: 'rotmoot',
    tier: 3,
    hp: 150,
    speed: 2.4,
    radius: 0.32,
    armour: ArmourType.Leather,
    damage: 22,
    dmgType: DamageType.Poison,
    range: 4.6,
    attacksPerSec: 0.5,
    splash: 1.2,
    flags: UF.Flying | UF.Ranged,
    companySize: 12,
    threat: 34,
    ability: 'plagueaura',
    voice: 'beast',
    vis: { build: 'flier', weapon: 'none', scale: 1.0, pauldron: 1 },
  }),

  // ── The Drowned Court (invader) ──────────────────────────────────────────
  u({
    id: 'drowned_thrall',
    name: 'Drowned Thrall',
    flavour: 'Walked here along the bottom of a sea that has not existed for an age.',
    faction: 'drowned',
    hp: 130,
    speed: 1.2,
    armour: ArmourType.Mail,
    damage: 13,
    dmgType: DamageType.Pierce,
    attacksPerSec: 0.75,
    companySize: 31,
    threat: 13,
    vis: { build: 'infantry', weapon: 'spear', helm: 2, pauldron: 1 },
  }),
  u({
    id: 'drowned_lamprey',
    name: 'Lamprey Host',
    flavour: 'Attaches. Detaches with rather less of you than it found.',
    faction: 'drowned',
    tier: 2,
    hp: 88,
    speed: 2.4,
    radius: 0.24,
    armour: ArmourType.Unarmoured,
    damage: 15,
    dmgType: DamageType.Pierce,
    attacksPerSec: 1.3,
    companySize: 22,
    threat: 18,
    ability: 'leech',
    vis: { build: 'swarm', weapon: 'claw', scale: 0.88, pauldron: 0 },
  }),
  u({
    id: 'drowned_choir',
    name: 'Abyssal Choir',
    flavour: 'The song has three parts. You are expected to sing the third.',
    faction: 'drowned',
    tier: 3,
    hp: 140,
    speed: 1.1,
    armour: ArmourType.Leather,
    damage: 20,
    dmgType: DamageType.Arcane,
    range: 5.4,
    attacksPerSec: 0.45,
    flags: UF.Caster,
    companySize: 6,
    threat: 40,
    ability: 'madness',
    vis: { build: 'caster', weapon: 'staff', helm: 5, pauldron: 0, cloak: true },
  }),
  u({
    id: 'drowned_tideguard',
    name: 'Tideguard',
    flavour: 'Holds a line that the tide will eventually make redundant. Holds it anyway.',
    faction: 'drowned',
    tier: 3,
    hp: 300,
    speed: 1.0,
    armour: ArmourType.Shielded,
    damage: 26,
    dmgType: DamageType.Blunt,
    attacksPerSec: 0.65,
    flags: UF.Shielded | UF.SpellEater,
    companySize: 10,
    threat: 42,
    ability: 'anchor',
    vis: { build: 'infantry', weapon: 'hammer', shield: true, helm: 3, pauldron: 2, scale: 1.12 },
  }),
  u({
    id: 'drowned_abyssal',
    name: 'The Abyssal',
    flavour: 'Something came up with the Court, and the Court does not discuss it.',
    faction: 'drowned',
    tier: 4,
    hp: 1250,
    speed: 1.15,
    radius: 0.68,
    armour: ArmourType.Monstrous,
    damage: 76,
    dmgType: DamageType.Arcane,
    attacksPerSec: 0.55,
    splash: 1.6,
    flags: UF.Champion | UF.Large,
    companySize: 1,
    threat: 190,
    ability: 'madness',
    vis: { build: 'champion', weapon: 'claw', scale: 1.7, pauldron: 2, cloak: true },
  }),
  /**
   * `madness` is one of the three invader signatures that is actually wired —
   * it is set through `damageUnit`, the one door — so the Storm Ray's blows
   * genuinely turn a soldier on his own side for a few seconds. It is put on a
   * flier deliberately: the Court's fog was reachable only by things that had
   * to walk through the line to deliver it, and a maddening weapon that opens
   * over the *second* rank is a different problem for the player to solve.
   */
  u({
    id: 'drowned_stormray',
    name: 'Storm Ray',
    flavour: 'It swims. That the air consents to bear it is a discourtesy the Court has never been asked to explain.',
    faction: 'drowned',
    tier: 3,
    hp: 260,
    speed: 2.5,
    radius: 0.44,
    armour: ArmourType.Leather,
    damage: 30,
    dmgType: DamageType.Arcane,
    range: 4.4,
    attacksPerSec: 0.5,
    splash: 1.0,
    flags: UF.Flying | UF.Caster,
    companySize: 5,
    threat: 56,
    ability: 'madness',
    voice: 'beast',
    vis: { build: 'flier', weapon: 'staff', scale: 1.2, pauldron: 0, cloak: true },
  }),

  // ── Summons & specials ───────────────────────────────────────────────────
  u({
    id: 'summon_skeleton',
    name: 'Called Dead',
    flavour: 'Rose because it was asked politely, and because it was asked at all.',
    faction: 'common',
    hp: 46,
    speed: 1.45,
    armour: ArmourType.Unarmoured,
    damage: 8,
    dmgType: DamageType.Slash,
    attacksPerSec: 0.9,
    flags: UF.Undead | UF.Summoned,
    pop: 0,
    companySize: 1,
    threat: 4,
    vis: { build: 'swarm', weapon: 'sword', scale: 0.92, pauldron: 0, helm: 1 },
  }),
  u({
    id: 'summon_bramble',
    name: 'Bramble Wall',
    flavour: 'Grown in a hurry, and it shows in the thorns.',
    faction: 'common',
    hp: 180,
    speed: 0,
    radius: 0.45,
    armour: ArmourType.Structure,
    damage: 4,
    dmgType: DamageType.Pierce,
    range: 0.6,
    attacksPerSec: 1.0,
    flags: UF.Summoned,
    pop: 0,
    companySize: 1,
    threat: 0,
    ability: 'thorns',
    vis: { build: 'brute', weapon: 'none', scale: 1.0, pauldron: 0 },
  }),

  // ── Heroes ───────────────────────────────────────────────────────────────
  //
  // One model per class, matching `HeroClass.unitId` in `heroes.ts`. These are
  // the *base* bodies: the class's own `hp`/`damage`/`speed` multipliers and the
  // character's level are applied on top at spawn, so the catalogue entry stays
  // a plain unit and the character sheet stays the thing that grows.
  //
  // Cost and pop are zero on purpose: a hero is not recruited and does not eat
  // into the population cap. The `threat` is real, though — it is what a hero is
  // worth on a field, and since wages are derived from threat it also means the
  // general draws a stipend like everybody else.
  u({
    id: 'hero_warlord',
    name: 'Warlord',
    flavour: 'Promoted by acclamation, in the field, by men who had run out of officers.',
    faction: 'common',
    hp: 900,
    speed: 1.6,
    radius: 0.42,
    armour: ArmourType.Plate,
    damage: 52,
    dmgType: HERO_DAMAGE_TYPE.warlord,
    range: 0.72,
    attacksPerSec: 0.9,
    splash: 0.7,
    flags: HERO_UNIT_FLAGS,
    pop: 0,
    companySize: 1,
    threat: 120,
    stamina: 3,
    vis: { build: 'champion', weapon: 'sword', scale: 1.45, shield: true, pauldron: 2, cloak: true, banner: true, helm: 3 },
  }),
  u({
    id: 'hero_castellan',
    name: 'Castellan',
    flavour: 'Has never lost a siege, which she attributes to arithmetic.',
    faction: 'common',
    hp: 900,
    speed: 1.6,
    radius: 0.42,
    armour: ArmourType.Plate,
    damage: 52,
    dmgType: HERO_DAMAGE_TYPE.castellan,
    range: 0.68,
    attacksPerSec: 0.8,
    flags: HERO_UNIT_FLAGS,
    pop: 0,
    companySize: 1,
    threat: 120,
    stamina: 3,
    vis: { build: 'champion', weapon: 'hammer', scale: 1.4, shield: true, pauldron: 2, helm: 2 },
  }),
  u({
    id: 'hero_huntmaster',
    name: 'Huntmaster',
    flavour: 'Counts the enemy by smell and is seldom wrong.',
    faction: 'common',
    hp: 900,
    speed: 1.6,
    radius: 0.4,
    armour: ArmourType.Mail,
    damage: 52,
    dmgType: HERO_DAMAGE_TYPE.huntmaster,
    range: 7.4,
    attacksPerSec: 1.1,
    flags: HERO_UNIT_FLAGS | UF.Ranged,
    pop: 0,
    companySize: 1,
    threat: 120,
    stamina: 3,
    vis: { build: 'champion', weapon: 'bow', scale: 1.32, pauldron: 1, cloak: true, helm: 4 },
  }),
  u({
    id: 'hero_sorcerer',
    name: 'Sorcerer',
    flavour: 'Keeps a tower, and a very short list of people who may knock.',
    faction: 'common',
    hp: 900,
    speed: 1.6,
    radius: 0.4,
    armour: ArmourType.Leather,
    damage: 52,
    dmgType: HERO_DAMAGE_TYPE.sorcerer,
    range: 6.2,
    attacksPerSec: 0.75,
    splash: 0.9,
    flags: HERO_UNIT_FLAGS | UF.Ranged | UF.Caster,
    pop: 0,
    companySize: 1,
    threat: 120,
    stamina: 3,
    vis: { build: 'champion', weapon: 'staff', scale: 1.3, pauldron: 1, cloak: true, helm: 5 },
  }),
  u({
    id: 'hero_prelate',
    name: 'Prelate',
    flavour: 'Carries the ninth bell’s clapper in a satchel and will not explain how.',
    faction: 'common',
    hp: 900,
    speed: 1.6,
    radius: 0.42,
    armour: ArmourType.Mail,
    damage: 52,
    dmgType: HERO_DAMAGE_TYPE.prelate,
    range: 0.72,
    attacksPerSec: 0.85,
    splash: 0.8,
    flags: HERO_UNIT_FLAGS,
    pop: 0,
    companySize: 1,
    threat: 120,
    stamina: 3,
    vis: { build: 'champion', weapon: 'scythe', scale: 1.38, pauldron: 2, cloak: true, helm: 1 },
  }),
  // Industrial roster additions are appended to preserve saved type indices.
  u({
    id: 'pavise_company', name: 'Pavise Company', faction: 'common', tier: 2,
    flavour: 'Advance the shield, wind the bow, loose together. Nobody calls it quick.',
    hp: 135, speed: 1.05, radius: 0.29, armour: ArmourType.Mail,
    damage: 28, dmgType: DamageType.Pierce, range: 7.4, attacksPerSec: 0.42,
    flags: UF.Ranged | UF.Shielded, ammo: 22, companySize: 14, threat: 27,
    cost: { grain: 12, steel: 2, tools: 0.5, planks: 2 },
    vis: { build: 'archer', weapon: 'crossbow', shield: true, helm: 2, pauldron: 1, scale: 1.0 },
  }),
  u({
    id: 'breach_sappers', name: 'Breach Sappers', faction: 'common', tier: 3,
    flavour: 'They bring tools to a battle and leave through the hole they made.',
    hp: 145, speed: 1.55, radius: 0.3, armour: ArmourType.Leather,
    damage: 68, dmgType: DamageType.Siege, attacksPerSec: 0.65,
    flags: UF.Siege | UF.Sapper, companySize: 6, pop: 2, threat: 43,
    cost: { grain: 15, tools: 3, armaments: 2, charcoal: 3 }, parts: { fitting: 1 },
    vis: { build: 'infantry', weapon: 'hammer', helm: 2, pauldron: 1, scale: 0.95 },
  }),
  u({
    id: 'ember_mortar', name: 'Ember Mortar', faction: 'common', tier: 4,
    flavour: 'A short barrel with a long reach. The crews mark where their own men are standing.',
    hp: 340, speed: 0.6, radius: 0.55, armour: ArmourType.Structure,
    damage: 92, dmgType: DamageType.Fire, range: 11.2, attacksPerSec: 0.18,
    windup: 0.8, splash: 3.1, flags: UF.Siege | UF.Ranged, ammo: 14,
    companySize: 2, pop: 4, threat: 106,
    cost: { grain: 24, armaments: 5, charcoal: 10 }, parts: { frame: 3, fitting: 4, gilded_core: 1 },
    vis: { build: 'siege', weapon: 'gun', scale: 1.35, pauldron: 0 },
  }),
  u({
    id: 'nullwing_hunters', name: 'Nullwing Hunters', faction: 'common', tier: 5,
    flavour: 'The cage opens above the battlements. They hunt the light before they hunt its bearer.',
    hp: 380, speed: 2.5, radius: 0.4, armour: ArmourType.Mail,
    damage: 42, dmgType: DamageType.Arcane, attacksPerSec: 0.8,
    flags: UF.Flying | UF.SpellEater, companySize: 4, pop: 3, threat: 97,
    cost: { grain: 20, armaments: 4, gravesilver: 3 }, parts: { sigil_frame: 1, focusing_ring: 1, hollow_heart: 1 },
    vis: { build: 'flier', weapon: 'claw', scale: 1.1, helm: 4, pauldron: 1 },
  }),
];

const UNIT_BY_ID = new Map<string, number>();
UNIT_TYPES.forEach((t, i) => UNIT_BY_ID.set(t.id, i));

export function unitTypeIndex(id: string): number {
  const i = UNIT_BY_ID.get(id);
  if (i === undefined) throw new Error(`Unknown unit type: ${id}`);
  return i;
}

export function unitType(index: number): UnitType {
  return UNIT_TYPES[index] ?? UNIT_TYPES[0];
}

export function unitTypeById(id: string): UnitType {
  return UNIT_TYPES[unitTypeIndex(id)];
}

export function unitsOfFaction(factionId: string): UnitType[] {
  return UNIT_TYPES.filter((t) => t.faction === factionId);
}

export const VILLAGER_TYPE = unitTypeIndex('villager');
